import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, DialogActions, DialogTitle, Grid, TextField, Typography, Checkbox, FormControlLabel, Autocomplete, FormGroup, Stack, IconButton, Backdrop, Chip, CircularProgress } from '@mui/material';
import TmFullButton from '../../common/buttons/TmFullButton';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { EditTerminalFormDto, emptyEditTerminalFormDto, mapToAddTerminalDto, mapToEditTerminalDto, mapToEditTerminalFormDto } from '../../../models/terminals/editTerminalFormDto';
import { CompanyShortDto } from '../../../models/companies/companyShortDto';
import { companiesService } from '../../../services/companiesService';
import { terminalsService } from '../../../services/terminalsService';
import { TerminalDetailsDto } from '../../../models/terminals/terminalDetailsDto';
import ErrorAlert from '../../common/ErrorAlert';
import { TextFieldFormControl } from '../../common/form/TextFieldFormControl';
import { TextAreaFormControl } from '../../common/form/TextAreaFormControl';
import { TmSwitchFormControl } from '../../common/form/TmSwitchFormControl';
import TmCross from '../../../icons/TmCross';
import Spinner from '../../common/Spinner';
import { Terminal } from '../../companies/add/models/terminal';
import { ShowForRoles } from '../../common/permissions/ShowForRoles';
import { RoleType } from '../../../models/enums/roleType';
import { TerminalTypeDto } from '../../../models/terminals/terminalTypeDto';
import { TerminalUseTypeDto } from '../../../models/terminals/terminalUseTypeDto';
import { DatePickerFormControl } from '../../common/form/DatePickerFormControl';
import StyledTabs from '../../common/tabs/StyledTabs';
import StyledTab from '../../common/tabs/StyledTab';
import { permissionService } from '../../../services/permissionService';

const CONFIG_JSON_ERROR = 'Конфигурация должна быть корректным JSON-объектом';
const CONFIG_JSON_EMPTY_ERROR = 'Конфигурация должна содержать хотя бы одно поле';

const COMPANY_SEARCH_PAGE_SIZE = 10;
const COMPANY_SEARCH_MIN_LENGTH = 3;

interface EditTerminalModalProps {
    open: boolean;
    onClose: () => void;
    onRefresh?: () => void;
    onAddingSuccess?: (terminal: Terminal) => void;
    isAdding: boolean;
    terminal?: TerminalDetailsDto;
    companyId?: number;
}

export const EditTerminalModal: React.FC<EditTerminalModalProps> = ({ open, onClose, onRefresh, onAddingSuccess, isAdding, terminal, companyId }) => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [togglingIntegration, setTogglingIntegration] = useState(false);
    const [companies, setCompanies] = useState<CompanyShortDto[]>([]);
    const [initialCompanies, setInitialCompanies] = useState<CompanyShortDto[]>([]);
    const [selectedCompanies, setSelectedCompanies] = useState<CompanyShortDto[]>([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [terminalTypes, setTerminalTypes] = useState<TerminalTypeDto[]>([]);
    const [terminalUseTypes, setTerminalUseTypes] = useState<TerminalUseTypeDto[]>([]);
    const [tab, setTab] = useState(0);
    const [configText, setConfigText] = useState('');
    const [configLoading, setConfigLoading] = useState(false);
    const [configLoaded, setConfigLoaded] = useState(false);
    const isAdmin = permissionService.hasRole(RoleType.Admin);
    const showConfigTab = !isAdding && isAdmin;

    const methods = useForm<EditTerminalFormDto>({
        defaultValues: terminal ? mapToEditTerminalFormDto(terminal) : emptyEditTerminalFormDto(companyId),
    });

    useEffect(() => {
        if (open) {
            const defaultValues = isAdding ? emptyEditTerminalFormDto(companyId) : mapToEditTerminalFormDto(terminal!);
            methods.reset(defaultValues);
            setTab(0);
            setConfigText('');
            setConfigLoaded(false);
            if (!companyId) {
                setSelectedCompanies(terminal?.companies ?? []);
            }
        }
    }, [open, isAdding, terminal, methods, companyId]);

    useEffect(() => {
        if (!open) {
            return;
        }

        if (companyId) {
            setCompaniesLoading(true);
            companiesService
                .search({ companyIds: [companyId], page: 0, pageSize: COMPANY_SEARCH_PAGE_SIZE })
                .then(r => {
                    const items = r.data.data.items;
                    setSelectedCompanies(items);
                    setCompanies([]);
                    setInitialCompanies([]);
                })
                .catch(e => console.log(e))
                .finally(() => setCompaniesLoading(false));
            return;
        }

        setCompaniesLoading(true);
        companiesService
            .search({ name: '', page: 0, pageSize: COMPANY_SEARCH_PAGE_SIZE })
            .then(r => {
                const items = r.data.data.items;
                setCompanies(items);
                setInitialCompanies(items);
            })
            .catch(e => {
                console.log(e);
                setCompanies([]);
                setInitialCompanies([]);
            })
            .finally(() => setCompaniesLoading(false));
    }, [open, companyId, terminal?.id]);

    const handleCompaniesInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
        if (companyId) {
            return;
        }

        if (newInputValue.length >= COMPANY_SEARCH_MIN_LENGTH) {
            setCompaniesLoading(true);
            companiesService
                .search({ name: newInputValue, page: 0, pageSize: COMPANY_SEARCH_PAGE_SIZE })
                .then(r => setCompanies(r.data.data.items))
                .catch(() => setCompanies(initialCompanies))
                .finally(() => setCompaniesLoading(false));
        } else {
            setCompanies(initialCompanies);
        }
    };

    useEffect(() => {
        if (!isAdding) {
            terminalsService.getTerminalTypes().then(r => setTerminalTypes(r.data.data)).catch(() => setTerminalTypes([]));
            terminalsService.getTerminalUseTypes().then(r => setTerminalUseTypes(r.data.data)).catch(() => setTerminalUseTypes([]));
        }
    }, [isAdding]);

    const handleToggleIntegration = async (enabled: boolean) => {
        if (isAdding || !terminal?.id) return;

        const formData = methods.getValues();
        
        if (enabled) {
            // Проверка обязательных полей перед включением
            if (!formData.terminalTypeId || !formData.useTypeId) {
                showAlert("Для включения интеграции с НИИАТ необходимо указать тип медицинского изделия и сценарий использования");
                methods.setValue('isNiiatIntegrationEnabled', false);
                return;
            }

            // Сначала сохраняем terminalTypeId и useTypeId в терминале
            try {
                await terminalsService.edit({
                    ...mapToEditTerminalDto(formData),
                    erpInfo: {
                        ...mapToEditTerminalDto(formData).erpInfo,
                        terminalTypeId: formData.terminalTypeId,
                        useTypeId: formData.useTypeId,
                    }
                });
            } catch (e: any) {
                console.log(e);
                const errorMessage = e.response?.data?.message ?? e.message ?? 'Ошибка при сохранении данных терминала';
                showAlert(errorMessage);
                methods.setValue('isNiiatIntegrationEnabled', false);
                return;
            }
        }

        setTogglingIntegration(true);
        try {
            await terminalsService.toggleNiiatIntegration(terminal.id, enabled);
            
            // Обновляем значение в форме
            methods.setValue('isNiiatIntegrationEnabled', enabled);
            
            if (enabled) {
                // После успешного включения обновляем данные терминала для получения AisErpId
                const updatedTerminal = await terminalsService.getById(terminal.id);
                const terminalData = updatedTerminal.data.data;
                methods.setValue('aisErpId', terminalData.erpInfo?.aisErpId ?? '');
            } else {
                // При выключении очищаем AisErpId
                methods.setValue('aisErpId', '');
            }
            
            // Обновляем данные терминала для отображения
            if (onRefresh) {
                onRefresh();
            }
        } catch (e: any) {
            console.log(e);
            const errorMessage = e.response?.data?.message ?? e.message ?? 'Ошибка при переключении интеграции';
            showAlert(errorMessage);
            // Откатываем тоггл при ошибке
            methods.setValue('isNiiatIntegrationEnabled', !enabled);
        } finally {
            setTogglingIntegration(false);
        }
    };

    const showAlert = (errorMessage: string = 'Произошла ошибка') => {
        setError(errorMessage);
    };

    const loadConfig = (terminalId: number) => {
        setConfigLoading(true);
        terminalsService.getConfig(terminalId)
            .then(r => {
                setConfigText(r.data.data.configJson ?? '');
                setConfigLoaded(true);
            })
            .catch(e => {
                const message = e.response?.data?.message ?? e.message ?? 'Произошла ошибка';
                showAlert(message);
            })
            .finally(() => setConfigLoading(false));
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
        setError('');
        if (newValue === 1 && !configLoaded && !configLoading && terminal?.id) {
            loadConfig(terminal.id);
        }
    };

    const validateConfigText = (text: string) => {
        if (!text.trim()) {
            return CONFIG_JSON_ERROR;
        }

        try {
            const parsed = JSON.parse(text);
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                return CONFIG_JSON_ERROR;
            }

            if (Object.keys(parsed).length === 0) {
                return CONFIG_JSON_EMPTY_ERROR;
            }
        } catch {
            return CONFIG_JSON_ERROR;
        }

        return '';
    };

    const saveConfig = () => {
        if (!terminal?.id) {
            return;
        }

        const validationError = validateConfigText(configText);
        if (validationError) {
            showAlert(validationError);
            return;
        }

        setLoading(true);
        terminalsService.updateConfig(terminal.id, configText)
            .then(() => {
                onClose();
                if (onRefresh) {
                    onRefresh();
                }
            })
            .catch(e => {
                const message = e.response?.data?.message ?? e.message ?? CONFIG_JSON_ERROR;
                showAlert(message);
            })
            .finally(() => setLoading(false));
    };

    const onSubmit = (formData: EditTerminalFormDto) => {
        setLoading(true);

        const serviceCall = isAdding
            ? terminalsService.add(mapToAddTerminalDto(formData))
            : terminalsService.edit(mapToEditTerminalDto(formData));

        serviceCall
            .then(r => {
                onClose();
                if (onRefresh) {
                    onRefresh();
                }
                if (isAdding && onAddingSuccess) {
                    const addedTerminal = {
                        id: r.data.data,
                        name: formData.name,
                        number: formData.number,
                        address: formData.address,
                    };
                    onAddingSuccess(addedTerminal);
                }
            })
            .catch(e => {
                console.log(e);
                const error = e.response.data.message ?? e.message;
                showAlert(error);
            })
            .finally(() => setLoading(false));
    };

    const onSubmitError = (errors: any) => {
        showAlert("Исправьте ошибки ввода");
        console.log('Form Errors:', errors);
    };

    return (
        <Box>
            <FormProvider {...methods}>
                <Dialog
                    open={open}
                    onClose={onClose}
                    fullWidth={showConfigTab}
                    maxWidth="sm"
                    PaperProps={{
                        sx: {
                            borderRadius: "25px",
                            ...(showConfigTab ? { height: "calc(100% - 64px)", maxHeight: "calc(100% - 64px)" } : {}),
                        },
                    }}
                >
                    {error && (
                        <ErrorAlert
                            message={error}
                            onClose={() => setError('')}
                        />
                    )}
                    <DialogTitle variant='h5' sx={{ textAlign: 'center' }}>{isAdding ? "Добавление нового терминала" : "Редактирование терминала"}</DialogTitle>
                    {!error && (<IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                            zIndex: 10000
                        }}
                    >
                        <TmCross fill="none" stroke="#cccdd5" />
                    </IconButton>
                    )}
                    <DialogContent sx={showConfigTab ? { display: "flex", flexDirection: "column", overflow: "hidden" } : undefined}>
                        {showConfigTab && (
                            <StyledTabs value={tab} onChange={handleTabChange}>
                                <StyledTab label="Основная информация" />
                                <StyledTab label="Конфигурация" />
                            </StyledTabs>
                        )}
                        <Box
                            component="form"
                            noValidate
                            sx={showConfigTab ? {
                                flex: 1,
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                                overflow: tab === 1 ? "hidden" : "auto",
                            } : undefined}
                        >
                            {tab === 1 && showConfigTab ? (
                                <Box sx={{ mt: 2, flex: 1, minHeight: 0, display: "flex" }}>
                                    {configLoading ? (
                                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : (
                                        <TextField
                                            value={configText}
                                            onChange={e => setConfigText(e.target.value)}
                                            multiline
                                            fullWidth
                                            sx={{
                                                flex: 1,
                                                "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" },
                                                "& textarea": { height: "100% !important", overflow: "auto !important", boxSizing: "border-box" },
                                            }}
                                            InputProps={{ sx: { fontFamily: "monospace", height: "100%" } }}
                                        />
                                    )}
                                </Box>
                            ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormGroup>

                                        <Typography variant='h6'>Общая информация</Typography>
                                        <Stack spacing={2} direction="column">
                                            <TextFieldFormControl
                                                name="name"
                                                label="Название"
                                                required
                                                maxLength={50}
                                            />
                                            <TextFieldFormControl
                                                name="number"
                                                label="Серийный номер"
                                                required
                                                maxLength={50}
                                            />

                                            {(isAdding || terminal?.version === "v2") &&
                                                <TextFieldFormControl
                                                    name="hardwareId"
                                                    label="HardwareId"
                                                    required
                                                    maxLength={100}
                                                />
                                            }
                                            <TextAreaFormControl
                                                name="description"
                                                label="Описание"
                                                rows={5}
                                                maxLength={1000}
                                            />
                                        </Stack>
                                    </FormGroup>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormGroup>

                                        <Typography variant='h6' sx={{ mt: 2 }}>Адрес и координаты</Typography>
                                        <Stack spacing={2} direction="column">
                                            <TextFieldFormControl
                                                name="address"
                                                label="Адрес"
                                                required
                                                maxLength={1000}
                                            />
                                            <Grid container>
                                                <Grid item xs={6}>
                                                    <TextFieldFormControl
                                                        name="latitude"
                                                        label="Широта"
                                                        type="number"
                                                        step="0.000001"
                                                        validationRules={{
                                                            min: {
                                                                value: -90,
                                                                message: "Широта должна быть в диапазоне от -90 до 90"
                                                            },
                                                            max: {
                                                                value: 90,
                                                                message: "Широта должна быть в диапазоне от -90 до 90"
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sx={{ pl: 2 }}>
                                                    <TextFieldFormControl
                                                        name="longitude"
                                                        label="Долгота"
                                                        type="number"
                                                        step="0.000001"
                                                        validationRules={{
                                                            min: {
                                                                value: -180,
                                                                message: "Долгота должна быть в диапазоне от -180 до 180"
                                                            },
                                                            max: {
                                                                value: 180,
                                                                message: "Долгота должна быть в диапазоне от -180 до 180"
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Stack>

                                        <Typography variant='h6' sx={{ mt: 2 }}>Клиенты</Typography>
                                        <Stack spacing={2} direction="column">
                                            <Controller
                                                name='companyIds'
                                                control={methods.control}
                                                defaultValue={[]}
                                                rules={{ required: 'Поле обязательно для заполнения' }}
                                                render={({ field: { onChange } }) => (
                                                    <Autocomplete
                                                        multiple
                                                        size="small"
                                                        id="companyIds"
                                                        options={companyId ? [] : companies}
                                                        filterOptions={(options) => options}
                                                        filterSelectedOptions
                                                        getOptionLabel={(item) => item?.name ?? ''}
                                                        getOptionKey={(item) => item.id}
                                                        isOptionEqualToValue={(option, item) => option.id === item.id}
                                                        value={selectedCompanies}
                                                        onChange={(_, newValue) => {
                                                            if (!companyId) {
                                                                setSelectedCompanies(newValue);
                                                                onChange(newValue.map(company => company.id));
                                                            }
                                                        }}
                                                        onInputChange={handleCompaniesInputChange}
                                                        loading={companiesLoading}
                                                        loadingText="Поиск..."
                                                        noOptionsText={companyId ? '' : 'Не найдено'}
                                                        disabled={!!companyId}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label='Клиенты'
                                                                error={Boolean(methods.formState.errors.companyIds)}
                                                                helperText={methods.formState.errors.companyIds?.message}
                                                                placeholder="Введите от 3х букв"
                                                                required
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: (
                                                                        <>
                                                                            {companiesLoading ? (
                                                                                <CircularProgress color="inherit" size={20} />
                                                                            ) : null}
                                                                            {params.InputProps.endAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                        renderTags={(value, getTagProps) =>
                                                            value.map((option, index) => (
                                                                <Chip
                                                                    label={option.name}
                                                                    {...getTagProps({ index })}
                                                                    key={option.id}
                                                                />
                                                            ))
                                                        }
                                                    />
                                                )}
                                            />
                                        </Stack>
                                    </FormGroup>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormGroup>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant='h6'>Статус</Typography>
                                                <Controller
                                                    name='isNotActive'
                                                    control={methods.control}
                                                    defaultValue={false}
                                                    render={({ field }) => (
                                                        <FormControlLabel
                                                            control={<Checkbox {...field} checked={field.value} />}
                                                            label="Не активен"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            {!isAdding && (
                                                <ShowForRoles roles={[RoleType.Admin]}>
                                                    <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                        <Typography variant='h6' sx={{ alignSelf: 'flex-end' }}>Интеграция с НИИАТ</Typography>
                                                        <Box>
                                                            <TmSwitchFormControl
                                                                name="isNiiatIntegrationEnabled"
                                                                label=""
                                                                disabled={togglingIntegration || loading}
                                                                onChange={handleToggleIntegration}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </ShowForRoles>
                                            )}
                                        </Grid>
                                    </FormGroup>
                                </Grid>
                                {!isAdding && (
                                    <ShowForRoles roles={[RoleType.Admin]}>
                                        <Grid item xs={12}>
                                            <FormGroup>
                                                <Typography variant='h6'>Сведения для НИИАТ</Typography>
                                                <Stack spacing={2} direction="column">
                                                    <TextFieldFormControl
                                                        name="aisErpId"
                                                        label="Идентификатор в НИИАТ"
                                                        maxLength={36}
                                                        disabled
                                                    />
                                                    <Controller
                                                        name="terminalTypeId"
                                                        control={methods.control}
                                                        render={({ field }) => (
                                                            <Autocomplete
                                                                size="small"
                                                                options={terminalTypes}
                                                                getOptionLabel={option => option?.name ?? ''}
                                                                value={terminalTypes.find(t => t.id === field.value) ?? null}
                                                                onChange={(_, newValue) => field.onChange(newValue ? newValue.id : null)}
                                                                renderInput={params => (
                                                                    <TextField {...params} label="Тип медицинского изделия" />
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                    <Controller
                                                        name="useTypeId"
                                                        control={methods.control}
                                                        render={({ field }) => (
                                                            <Autocomplete
                                                                size="small"
                                                                options={terminalUseTypes}
                                                                getOptionLabel={option => option?.name ?? ''}
                                                                value={terminalUseTypes.find(t => t.id === field.value) ?? null}
                                                                onChange={(_, newValue) => field.onChange(newValue ? newValue.id : null)}
                                                                renderInput={params => (
                                                                    <TextField {...params} label="Сценарий использования" />
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                    <TextFieldFormControl
                                                        name="checkupCertificateNumber"
                                                        label="Номер сертификата поверки"
                                                        maxLength={100}
                                                    />
                                                    <DatePickerFormControl
                                                        name="checkupCertificateDate"
                                                        label="Дата поверки"
                                                    />
                                                </Stack>
                                            </FormGroup>
                                        </Grid>
                                    </ShowForRoles>
                                )}
                            </Grid>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: 'center', paddingBottom: "2em" }}>
                        <TmFullButton
                            onClick={tab === 1 && showConfigTab ? saveConfig : methods.handleSubmit(onSubmit, onSubmitError)}
                            width='45%'
                            disabled={configLoading}
                        >
                            Сохранить
                        </TmFullButton>
                    </DialogActions>
                </Dialog>
            </FormProvider>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                open={loading}
            >
                <Spinner />
            </Backdrop>
        </Box>
    );
};